import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, Sparkles, Clock, AlertTriangle, ShieldCheck, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PageHeader, SectionCard, KpiCard } from "@/components/Primitives";

const BAND_STYLES = {
  green:  { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-900", dot: "bg-emerald-500", label: "Good" },
  yellow: { bg: "bg-amber-50",   border: "border-amber-200",   text: "text-amber-900",   dot: "bg-amber-500",   label: "Watch" },
  red:    { bg: "bg-red-50",     border: "border-red-200",     text: "text-red-900",     dot: "bg-red-500",     label: "Recover" },
  unknown:{ bg: "bg-brand-bg",   border: "border-brand",       text: "text-brand-muted", dot: "bg-brand-muted", label: "Start" },
};

export default function Wellbeing() {
  const navigate = useNavigate();
  const [risk, setRisk] = useState(null);
  const [trend, setTrend] = useState([]);
  const [interventions, setInterventions] = useState([]);
  const [recovered, setRecovered] = useState(null);
  const [checkinOpen, setCheckinOpen] = useState(false);
  const [activeIntervention, setActiveIntervention] = useState(null);
  const [energy, setEnergy] = useState(3);
  const [satisfaction, setSatisfaction] = useState(3);
  const [oneWord, setOneWord] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    const [r, t, i, rc] = await Promise.all([
      api.get("/wellbeing/risk"),
      api.get("/wellbeing/trend"),
      api.get("/wellbeing/interventions"),
      api.get("/wellbeing/recovered-time"),
    ]);
    setRisk(r.data); setTrend(t.data); setInterventions(i.data); setRecovered(rc.data);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/wellbeing/checkin", { energy, satisfaction, one_word: oneWord });
      toast.success("Check-in saved — private to you only");
      setCheckinOpen(false); setOneWord(""); setEnergy(3); setSatisfaction(3);
      load();
    } catch (err) { toast.error("Could not save check-in"); }
    finally { setSubmitting(false); }
  };

  const band = BAND_STYLES[risk?.band || "unknown"];

  return (
    <>
      <PageHeader
        eyebrow="Burnout Prevention"
        title="Your Wellbeing"
        subtitle="Private to you. Never shared with your employer. Never visible to anyone but you."
        actions={
          <button
            onClick={() => setCheckinOpen(true)}
            className="px-4 py-2 rounded-md bg-brand-primary text-white text-sm font-medium hover:bg-[#35096B]"
            data-testid="wellbeing-checkin-btn"
          >
            <Sparkles className="w-4 h-4 inline mr-1.5" />
            Daily check-in
          </button>
        }
        testid="wellbeing-header"
      />

      {/* Risk band + key metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className={`border ${band.border} ${band.bg} rounded-lg p-4`} data-testid="wellbeing-risk-band">
          <div className="text-[10px] uppercase tracking-wide opacity-75">Burnout risk</div>
          <div className="flex items-center gap-2 mt-1">
            <span className={`w-3 h-3 rounded-full ${band.dot}`} />
            <span className={`text-2xl font-semibold ${band.text}`}>{band.label}</span>
          </div>
          {risk?.score !== null && risk?.score !== undefined && (
            <div className={`text-xs ${band.text} opacity-80 mt-1`}>Composite: {risk.score}/5.0</div>
          )}
        </div>
        <KpiCard label="Check-ins (30d)" value={risk?.checkins_total ?? "—"} icon={Heart} hint="Daily pulse history" testid="wellbeing-checkins-total" />
        <KpiCard label="Hours recovered" value={recovered ? `${recovered.monthly_hours_recovered}h` : "—"} icon={Clock} hint="This month, returned to your life" testid="wellbeing-recovered" />
        <KpiCard label="Family dinners" value={recovered?.dinner_evenings ?? "—"} icon={ShieldCheck} hint="Estimated, based on time recovered" testid="wellbeing-dinners" />
      </div>

      {/* Burnout alert banner */}
      {risk?.alert && (
        <div className="border border-red-200 bg-red-50 rounded-lg p-4 mb-6 flex items-start gap-3" data-testid="wellbeing-alert">
          <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="font-semibold text-red-900">ARIA: I notice your wellbeing has trended down for {risk.streak_declining} days.</div>
            <div className="text-sm text-red-800 mt-1">Consider one of the 2-minute interventions below or block protected time this week. This is between you and me — no one else sees this.</div>
          </div>
        </div>
      )}

      {/* Trend strip */}
      <SectionCard title="30-day trend" testid="wellbeing-trend-section">
        {trend.length === 0 ? (
          <div className="text-sm text-brand-muted p-3">No check-ins yet. Tap <b>Daily check-in</b> to start.</div>
        ) : (
          <div className="flex items-end gap-1 h-32" data-testid="wellbeing-trend-chart">
            {trend.map((c, i) => {
              const score = (c.energy + c.satisfaction) / 2;
              const heightPct = (score / 5) * 100;
              const color = score >= 3.5 ? "bg-emerald-400" : score >= 2.5 ? "bg-amber-400" : "bg-red-400";
              return (
                <div key={i} className="flex-1 flex flex-col items-center justify-end" title={`${score.toFixed(1)} — ${new Date(c.timestamp).toLocaleDateString()}`}>
                  <div className={`w-full ${color} rounded-t`} style={{ height: `${heightPct}%` }} />
                </div>
              );
            })}
          </div>
        )}
      </SectionCard>

      <div className="h-6" />

      {/* Interventions */}
      <SectionCard title="2-minute micro-interventions" subtitle="Evidence-based, takes less time than a charting click." testid="wellbeing-interventions-section">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {interventions.map(iv => (
            <button
              key={iv.id}
              onClick={() => setActiveIntervention(iv)}
              className="text-left p-4 border border-brand rounded-lg hover:border-brand-secondary hover:bg-brand-bg/40 transition-colors"
              data-testid={`intervention-${iv.id}`}
            >
              <div className="font-medium text-sm text-brand-text">{iv.title}</div>
              <div className="text-xs text-brand-muted mt-1 line-clamp-2">{iv.body}</div>
            </button>
          ))}
        </div>
      </SectionCard>

      {/* Privacy footer */}
      <div className="mt-8 text-center text-xs text-brand-muted" data-testid="wellbeing-privacy">
        <ShieldCheck className="w-3.5 h-3.5 inline mr-1" />
        Your wellbeing data is private. It is never shared with your employer, payer, or any other party.
      </div>

      {/* Check-in modal */}
      {checkinOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60" onClick={() => setCheckinOpen(false)} data-testid="wellbeing-checkin-modal">
          <form onSubmit={submit} className="bg-white rounded-lg max-w-md w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div>
                <div className="eyebrow">End of shift</div>
                <h3 className="text-lg font-semibold">Three quick questions</h3>
              </div>
              <button type="button" onClick={() => setCheckinOpen(false)} data-testid="wellbeing-checkin-close"><X className="w-4 h-4 text-brand-muted" /></button>
            </div>
            <div>
              <label className="text-sm font-medium text-brand-text">Energy level <span className="text-brand-muted text-xs">({energy}/5)</span></label>
              <input type="range" min="1" max="5" value={energy} onChange={e => setEnergy(parseInt(e.target.value))} className="w-full" data-testid="wellbeing-energy-slider" />
              <div className="flex justify-between text-[10px] text-brand-muted px-1"><span>Drained</span><span>Energised</span></div>
            </div>
            <div>
              <label className="text-sm font-medium text-brand-text">Satisfaction <span className="text-brand-muted text-xs">({satisfaction}/5)</span></label>
              <input type="range" min="1" max="5" value={satisfaction} onChange={e => setSatisfaction(parseInt(e.target.value))} className="w-full" data-testid="wellbeing-satisfaction-slider" />
              <div className="flex justify-between text-[10px] text-brand-muted px-1"><span>Disengaged</span><span>Fulfilled</span></div>
            </div>
            <div>
              <label className="text-sm font-medium text-brand-text">One word about today (optional)</label>
              <input value={oneWord} onChange={e => setOneWord(e.target.value)} maxLength={40} placeholder="busy · steady · rewarding · heavy …"
                className="w-full mt-1 px-3 py-2 border border-brand rounded-md text-sm" data-testid="wellbeing-oneword" />
            </div>
            <button type="submit" disabled={submitting}
              className="w-full px-4 py-2.5 rounded-md bg-brand-primary text-white text-sm font-medium hover:bg-[#35096B] disabled:opacity-50"
              data-testid="wellbeing-checkin-submit">
              {submitting ? "Saving…" : "Save check-in"}
            </button>
            <div className="text-[10px] text-brand-muted text-center">
              Private to you only. Never shared.
            </div>
          </form>
        </div>
      )}

      {/* Intervention modal */}
      {activeIntervention && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60" onClick={() => setActiveIntervention(null)} data-testid="intervention-modal">
          <div className="bg-white rounded-lg max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-3">
              <h3 className="text-lg font-semibold">{activeIntervention.title}</h3>
              <button onClick={() => setActiveIntervention(null)}><X className="w-4 h-4 text-brand-muted" /></button>
            </div>
            <p className="text-sm text-brand-text leading-relaxed">{activeIntervention.body}</p>
            <button onClick={() => setActiveIntervention(null)} className="mt-4 w-full px-4 py-2 rounded-md bg-brand-primary text-white text-sm font-medium">Done — feel better</button>
          </div>
        </div>
      )}
    </>
  );
}
